import React, { useEffect, useState, useMemo } from "react";
import { api, fmtINR, fmtDate, daysBetween } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { Plus } from "lucide-react";

const RTYPES = ["Pay & Play", "Coaching", "Events", "Sponsorship", "Corporate", "Other"];
const ECATS = ["Maintenance", "Marketing", "Salaries", "Event Ops", "Utilities", "Vendor", "Other"];

const AgingCell = ({ dueDate, status }) => {
  if (status === "Paid") return <span className="text-emerald-700">Paid</span>;
  const days = daysBetween(dueDate); // positive = past due, negative = not yet due
  if (days < 0) return <span className="text-slate-500">Not yet due ({Math.abs(days)}d)</span>;
  let color = "text-slate-700";
  if (days > 60) color = "text-rose-700 font-semibold";
  else if (days > 45) color = "text-rose-600";
  else if (days > 30) color = "text-amber-700";
  return <span className={color}>{days}d overdue</span>;
};

const AddDialog = ({ triggerLabel, title, fields, onSave, testId }) => {
  const [open, setOpen] = useState(false);
  const initial = fields.reduce((a, f) => ({ ...a, [f.name]: f.default ?? "" }), {});
  const [f, setF] = useState(initial);
  const save = async () => { const ok = await onSave(f); if (ok !== false) { setOpen(false); setF(initial); } };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid={testId} className="bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4 mr-1" /> {triggerLabel}</Button></DialogTrigger>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {fields.map((fld) => (
            <div key={fld.name} className={fld.wide ? "col-span-2" : ""}>
              <Label>{fld.label}</Label>
              {fld.type === "select" ? (
                <Select value={f[fld.name]} onValueChange={(v) => setF({ ...f, [fld.name]: v })}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{fld.options.map((o) => <SelectItem key={o.value || o} value={o.value || o}>{o.label || o}</SelectItem>)}</SelectContent>
                </Select>
              ) : (
                <Input type={fld.type || "text"} value={f[fld.name]} onChange={(e) => setF({ ...f, [fld.name]: fld.type === "number" ? +e.target.value : e.target.value })} />
              )}
            </div>
          ))}
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} className="bg-blue-600 hover:bg-blue-700">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Finance() {
  const [revenue, setRevenue] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [receivables, setReceivables] = useState([]);
  const [venues, setVenues] = useState([]);
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);

  const load = async () => {
    const [r, e, rc, v, ev, u] = await Promise.all([
      api.get("/revenue"), api.get("/expenses"), api.get("/receivables"),
      api.get("/venues"), api.get("/events"), api.get("/users"),
    ]);
    setRevenue(r.data); setExpenses(e.data); setReceivables(rc.data);
    setVenues(v.data); setEvents(ev.data); setUsers(u.data);
  };
  useEffect(() => { load(); }, []);

  const totals = useMemo(() => ({
    rev: revenue.reduce((s, r) => s + Number(r.amount || 0), 0),
    exp: expenses.reduce((s, r) => s + Number(r.amount || 0), 0),
    rec: receivables.reduce((s, r) => s + Number(r.balance || 0), 0),
  }), [revenue, expenses, receivables]);

  return (
    <div className="space-y-6" data-testid="finance-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Finance</div>
        <h1 className="text-3xl font-black tracking-tight text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Money Movement</h1>
      </div>

      <div className="grid md:grid-cols-4 gap-4">
        <Card className="border-slate-200"><CardContent className="p-4"><div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Total Revenue</div><div className="text-2xl font-black mt-1 text-emerald-700">{fmtINR(totals.rev)}</div></CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-4"><div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Total Expenses</div><div className="text-2xl font-black mt-1 text-rose-700">{fmtINR(totals.exp)}</div></CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-4"><div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Net</div><div className={`text-2xl font-black mt-1 ${totals.rev - totals.exp >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{fmtINR(totals.rev - totals.exp)}</div></CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-4"><div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Outstanding Receivables</div><div className="text-2xl font-black mt-1 text-amber-700">{fmtINR(totals.rec)}</div></CardContent></Card>
      </div>

      <Tabs defaultValue="revenue">
        <TabsList>
          <TabsTrigger data-testid="tab-revenue" value="revenue">Revenue</TabsTrigger>
          <TabsTrigger data-testid="tab-expenses" value="expenses">Expenses</TabsTrigger>
          <TabsTrigger data-testid="tab-receivables" value="receivables">Receivables</TabsTrigger>
        </TabsList>

        <TabsContent value="revenue">
          <div className="flex justify-end mb-3">
            <AddDialog testId="add-revenue" triggerLabel="Add Revenue" title="New Revenue"
              fields={[
                { name: "description", label: "Description", wide: true },
                { name: "type", label: "Type", type: "select", options: RTYPES, default: "Pay & Play" },
                { name: "amount", label: "Amount (₹)", type: "number", default: 0 },
                { name: "date", label: "Date", type: "date", default: new Date().toISOString().slice(0, 10) },
                { name: "client", label: "Client" },
                { name: "status", label: "Status", type: "select", options: ["Received", "Pending"], default: "Received" },
                { name: "venue_id", label: "Venue", type: "select", options: [{ value: "", label: "None" }, ...venues.map((v) => ({ value: v.id, label: v.name }))] },
                { name: "event_id", label: "Event", type: "select", options: [{ value: "", label: "None" }, ...events.map((e) => ({ value: e.id, label: e.name }))] },
              ]}
              onSave={async (form) => {
                const v = venues.find((x) => x.id === form.venue_id);
                const e = events.find((x) => x.id === form.event_id);
                try { await api.post("/revenue", { ...form, venue_name: v?.name || null, event_name: e?.name || null }); toast.success("Revenue added"); load(); }
                catch { toast.error("Failed"); return false; }
              }} />
          </div>
          <Card className="border-slate-200"><Table>
            <TableHeader><TableRow><TableHead>Description</TableHead><TableHead>Type</TableHead><TableHead>Date</TableHead><TableHead>Venue / Event</TableHead><TableHead>Client</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Amount</TableHead></TableRow></TableHeader>
            <TableBody>
              {revenue.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-6">No revenue recorded.</TableCell></TableRow>}
              {revenue.map((r) => (<TableRow key={r.id}><TableCell>{r.description}</TableCell><TableCell>{r.type}</TableCell><TableCell>{fmtDate(r.date)}</TableCell><TableCell className="text-slate-500 text-xs">{r.venue_name || r.event_name || "—"}</TableCell><TableCell>{r.client}</TableCell><TableCell><StatusBadge status={r.status} /></TableCell><TableCell className="text-right font-mono">{fmtINR(r.amount)}</TableCell></TableRow>))}
            </TableBody>
          </Table></Card>
        </TabsContent>

        <TabsContent value="expenses">
          <div className="flex justify-end mb-3">
            <AddDialog testId="add-expense" triggerLabel="Add Expense" title="New Expense"
              fields={[
                { name: "description", label: "Description", wide: true },
                { name: "category", label: "Category", type: "select", options: ECATS, default: "Other" },
                { name: "amount", label: "Amount (₹)", type: "number", default: 0 },
                { name: "date", label: "Date", type: "date", default: new Date().toISOString().slice(0, 10) },
                { name: "vendor", label: "Vendor" },
                { name: "status", label: "Status", type: "select", options: ["Paid", "Pending"], default: "Paid" },
                { name: "venue_id", label: "Venue", type: "select", options: [{ value: "", label: "None" }, ...venues.map((v) => ({ value: v.id, label: v.name }))] },
                { name: "event_id", label: "Event", type: "select", options: [{ value: "", label: "None" }, ...events.map((e) => ({ value: e.id, label: e.name }))] },
              ]}
              onSave={async (form) => {
                const v = venues.find((x) => x.id === form.venue_id);
                const e = events.find((x) => x.id === form.event_id);
                try { await api.post("/expenses", { ...form, venue_name: v?.name || null, event_name: e?.name || null }); toast.success("Expense added"); load(); }
                catch { toast.error("Failed"); return false; }
              }} />
          </div>
          <Card className="border-slate-200"><Table>
            <TableHeader><TableRow><TableHead>Description</TableHead><TableHead>Category</TableHead><TableHead>Date</TableHead><TableHead>Vendor</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Amount</TableHead></TableRow></TableHeader>
            <TableBody>
              {expenses.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-6">No expenses recorded.</TableCell></TableRow>}
              {expenses.map((r) => (<TableRow key={r.id}><TableCell>{r.description}</TableCell><TableCell>{r.category}</TableCell><TableCell>{fmtDate(r.date)}</TableCell><TableCell>{r.vendor}</TableCell><TableCell><StatusBadge status={r.status} /></TableCell><TableCell className="text-right font-mono">{fmtINR(r.amount)}</TableCell></TableRow>))}
            </TableBody>
          </Table></Card>
        </TabsContent>

        <TabsContent value="receivables">
          <div className="flex justify-end mb-3">
            <AddDialog testId="add-receivable" triggerLabel="Add Receivable" title="New Receivable"
              fields={[
                { name: "client", label: "Client" },
                { name: "invoice_number", label: "Invoice #" },
                { name: "description", label: "Description", wide: true },
                { name: "invoice_amount", label: "Amount (₹)", type: "number", default: 0 },
                { name: "invoice_date", label: "Invoice Date", type: "date", default: new Date().toISOString().slice(0, 10) },
                { name: "due_date", label: "Due Date", type: "date", default: new Date().toISOString().slice(0, 10) },
                { name: "amount_received", label: "Amount Received", type: "number", default: 0 },
                { name: "status", label: "Status", type: "select", options: ["Pending", "Partial", "Paid", "Overdue"], default: "Pending" },
                { name: "responsible_id", label: "Responsible", type: "select", options: [{ value: "", label: "—" }, ...users.map((u) => ({ value: u.id, label: u.name }))] },
              ]}
              onSave={async (form) => {
                const u = users.find((x) => x.id === form.responsible_id);
                const bal = Number(form.invoice_amount) - Number(form.amount_received);
                try { await api.post("/receivables", { ...form, responsible_name: u?.name || "", balance: bal }); toast.success("Receivable added"); load(); }
                catch { toast.error("Failed"); return false; }
              }} />
          </div>
          <Card className="border-slate-200"><Table>
            <TableHeader><TableRow><TableHead>Client</TableHead><TableHead>Invoice #</TableHead><TableHead>Invoice Date</TableHead><TableHead>Due Date</TableHead><TableHead>Days Out</TableHead><TableHead className="text-right">Amount</TableHead><TableHead className="text-right">Balance</TableHead><TableHead>Status</TableHead><TableHead>Owner</TableHead></TableRow></TableHeader>
            <TableBody>
              {receivables.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-slate-500 py-6">No outstanding receivables.</TableCell></TableRow>}
              {receivables.map((r) => (<TableRow key={r.id}><TableCell className="font-medium">{r.client}</TableCell><TableCell className="font-mono text-xs">{r.invoice_number}</TableCell><TableCell>{fmtDate(r.invoice_date)}</TableCell><TableCell>{fmtDate(r.due_date)}</TableCell><TableCell><AgingCell dueDate={r.due_date} status={r.status} /></TableCell><TableCell className="text-right font-mono">{fmtINR(r.invoice_amount)}</TableCell><TableCell className="text-right font-mono font-semibold">{fmtINR(r.balance)}</TableCell><TableCell><StatusBadge status={r.status} /></TableCell><TableCell>{r.responsible_name}</TableCell></TableRow>))}
            </TableBody>
          </Table></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
